import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export interface QuestionGroup {
  /** e.g. "Part 1" or "Questions 1-13" */
  label: string;
  /** 1-based inclusive range of question numbers in this group. */
  from: number;
  to: number;
}

interface QuestionNavProps {
  /** Total number of questions in the test. */
  total: number;
  /** answered[i] === true when question i (0-based) has an answer. */
  answered: boolean[];
  /** Currently focused question (0-based), highlighted with a ring. */
  activeIndex?: number;
  /** Jump to a question by 0-based index (scroll it into view). */
  onJump: (index: number) => void;
  /**
   * Optional part grouping. When omitted, all questions render as one flat row.
   * Ranges are 1-based inclusive and should tile [1..total] without gaps.
   */
  groups?: QuestionGroup[];
  /** Optional prev / next part or question stepping. */
  onPrev?: () => void;
  onNext?: () => void;
}

/**
 * Sticky bottom navigation bar for a computer-based IELTS test, styled after
 * the engnovate part-switcher but in our brand colours. Shows numbered chips
 * grouped into parts; answered chips fill orange, the in-view question gets a
 * ring. Clicking a chip scrolls that question into view. Horizontally
 * scrollable so it stays usable on a phone.
 */
export function QuestionNav({
  total,
  answered,
  activeIndex,
  onJump,
  groups,
  onPrev,
  onNext,
}: QuestionNavProps) {
  const { t } = useTranslation();

  // Fall back to a single implicit group covering every question.
  const parts: QuestionGroup[] =
    groups && groups.length > 0
      ? groups
      : [{ label: t('ielts.nav.allQuestions'), from: 1, to: total }];

  const answeredCount = answered.filter(Boolean).length;

  return (
    <div className="sticky bottom-24 z-40 -mx-4 mt-2 rounded-card border border-outline-variant/25 bg-surface-container-lowest/95 px-3 py-2 shadow-[0_-4px_16px_rgba(30,41,59,0.06)] backdrop-blur sm:-mx-6 sm:px-4 md:bottom-0 md:rounded-none md:border-0 md:border-t">
      <div className="flex items-center gap-2">
        {/* Prev / Next steppers */}
        {(onPrev || onNext) && (
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={onPrev}
              disabled={!onPrev}
              aria-label={t('ielts.nav.prev')}
              className="grid h-8 w-8 place-items-center rounded-button bg-surface-container text-on-surface ghost-border transition hover:bg-surface-container-high disabled:opacity-40"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!onNext}
              aria-label={t('ielts.nav.next')}
              className="grid h-8 w-8 place-items-center rounded-button bg-surface-container text-on-surface ghost-border transition hover:bg-surface-container-high disabled:opacity-40"
            >
              <span className="material-symbols-outlined text-base">arrow_forward</span>
            </button>
          </div>
        )}

        {/* Answered counter */}
        <span className="hidden shrink-0 text-[11px] font-semibold uppercase tracking-label text-on-surface-variant sm:block">
          {t('ielts.nav.answeredOf', { answered: answeredCount, total })}
        </span>

        {/* Scrollable part groups + chips */}
        <div className="flex min-w-0 flex-1 gap-3 overflow-x-auto pb-0.5">
          {parts.map((part) => (
            <div
              key={part.label}
              className="flex shrink-0 items-center gap-2 rounded-pill bg-surface-container-low px-2 py-1 ghost-border"
            >
              <span className="shrink-0 pl-1 text-[11px] font-semibold text-on-surface-variant">
                {part.label}
              </span>
              <div className="flex gap-1">
                {range(part.from, part.to).map((n) => {
                  const idx = n - 1;
                  const isAnswered = answered[idx];
                  const isActive = activeIndex === idx;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => onJump(idx)}
                      className={cn(
                        'grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-semibold tabular-nums transition',
                        isAnswered
                          ? 'bg-primary text-on-primary'
                          : 'bg-surface-container-lowest text-on-surface-variant ghost-border hover:bg-surface-container',
                        isActive && 'ring-2 ring-primary ring-offset-1 ring-offset-surface-container-lowest',
                      )}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function range(from: number, to: number): number[] {
  const out: number[] = [];
  for (let n = from; n <= to; n += 1) out.push(n);
  return out;
}
